import { useEffect, useRef, useState } from 'react';
import { testimonials } from '@/mocks/homepage';

export default function TestimonialsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="testimonials" ref={ref} className="bg-cream-100 py-24 md:py-36 relative overflow-hidden">
      {/* Decorative shapes */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-sage-200/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-48 h-48 bg-sand-200/20 rounded-full blur-3xl" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-16 md:mb-24 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-sage-500 text-xs tracking-[0.3em] uppercase mb-4 font-medium">
              Their Stories
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal-300 font-medium leading-tight max-w-3xl mx-auto">
              People finding
              <br />
              <span className="italic font-light">peace in practice.</span>
            </h2>
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                className={`bg-white rounded-2xl p-8 md:p-10 transition-all duration-700 hover:shadow-[0_4px_24px_rgba(0,0,0,0.04)] ${
                  visible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${200 + i * 150}ms` }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden bg-sage-100 flex-shrink-0">
                    <img
                      src={t.imageUrl}
                      alt={t.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h4 className="text-charcoal-300 font-medium text-base">{t.name}</h4>
                    <p className="text-charcoal-50 text-sm">{t.role}</p>
                    <p className="text-sage-400 text-xs mt-0.5">{t.location}</p>
                  </div>
                </div>

                <div className="relative">
                  <i className="ri-double-quotes-l text-sage-200 text-3xl absolute -top-2 -left-1" />
                  <p className="text-charcoal-100 text-sm md:text-base leading-relaxed pl-6">
                    {t.story}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}